import { z } from 'zod';
import { AccountSchema, AccountTableItemSchema } from '../types/account';
import { FeatureDetailSchema } from '../types/features';
import { BrandingSchema } from '../types/branding';

export namespace CreateAccount {
  export const RequestBodySchema = z.object({
    account: AccountSchema.omit({ id: true, createdOn: true, identifier: true }),
  });
  export type RequestBody = z.infer<typeof RequestBodySchema>;

  export const ResponseSchema = z.object({ account: AccountSchema });
  export type Response = z.infer<typeof ResponseSchema>;
}

export namespace GetAccounts {
  export const ResponseSchema = z.object({
    accounts: z.array(AccountTableItemSchema),
  });
  export type Response = z.infer<typeof ResponseSchema>;
}

export namespace GetAccount {
  export const ParamsSchema = z.object({
    id: z.coerce.number(),
  });
  export type Params = z.infer<typeof ParamsSchema>;

  export const ResponseSchema = z.object({
    account: AccountSchema,
    features: FeatureDetailSchema.array(),
  });
  export type Response = z.infer<typeof ResponseSchema>;
}

export namespace UpdateAccount {
  export const ParamsSchema = z.object({
    id: z.coerce.number(),
  });
  export type Params = z.infer<typeof ParamsSchema>;

  export const RequestBodySchema = z.object({
    account: AccountSchema.omit({ id: true, createdOn: true, identifier: true }),
  });
  export type RequestBody = z.infer<typeof RequestBodySchema>;

  export const ResponseSchema = z.object({ account: AccountSchema });
  export type Response = z.infer<typeof ResponseSchema>;
}

export namespace UpsertAccountFeature {
  export const ParamsSchema = z.object({
    id: z.coerce.number(),
  });
  export type Params = z.infer<typeof ParamsSchema>;

  export const RequestBodySchema = z.object({
    propName: z.string(),
    value: z.string(),
  });
  export type RequestBody = z.infer<typeof RequestBodySchema>;

  export const ResponseSchema = z.object({
    feature: FeatureDetailSchema,
  });
  export type Response = z.infer<typeof ResponseSchema>;
}

export namespace GetAccountBranding {
  export const ParamsSchema = z.object({
    id: z.coerce.number(),
  });
  export type Params = z.infer<typeof ParamsSchema>;

  export const ResponseSchema = z.object({
    branding: BrandingSchema,
  });
  export type Response = z.infer<typeof ResponseSchema>;
}

export namespace CreatePalette {
  export const ParamsSchema = z.object({
    id: z.coerce.number(),
  });
  export type Params = z.infer<typeof ParamsSchema>;

  export const RequestBodySchema = z.object({
    name: z.string(),
    data: z.object({
      colors: z.array(z.string()),
    }),
    categoryId: z.number(),
  });
  export type RequestBody = z.infer<typeof RequestBodySchema>;
}

export namespace UpdatePalette {
  export const ParamsSchema = z.object({
    id: z.coerce.number(),
    paletteId: z.coerce.number(),
  });
  export type Params = z.infer<typeof ParamsSchema>;

  export const RequestBodySchema = z.object({
    name: z.string(),
    data: z.object({
      colors: z.array(z.string()),
    }),
  });
  export type RequestBody = z.infer<typeof RequestBodySchema>;
}

export namespace DeletePalette {
  export const ParamsSchema = z.object({
    id: z.coerce.number(),
    paletteId: z.coerce.number(),
  });
  export type Params = z.infer<typeof ParamsSchema>;
}

export namespace UpdateThemePalette {
  export const ParamsSchema = z.object({
    id: z.coerce.number(),
    themeId: z.coerce.number(),
  });
  export type Params = z.infer<typeof ParamsSchema>;

  export const RequestBodySchema = z.object({
    paletteId: z.coerce.number(),
  });
  export type RequestBody = z.infer<typeof RequestBodySchema>;
}

export namespace UpdateThemeColors {
  export const ParamsSchema = z.object({
    id: z.coerce.number(),
  });
  export type Params = z.infer<typeof ParamsSchema>;

  export const RequestBodySchema = z.object({
    colors: z.object({
      primary: z.string().nullable(),
      bubble: z.string().nullable(),
      prompt: z.string().nullable(),
    }),
  });
  export type RequestBody = z.infer<typeof RequestBodySchema>;
}

export namespace ToggleTheme {
  export const ParamsSchema = z.object({
    id: z.coerce.number(),
  });
  export type Params = z.infer<typeof ParamsSchema>;

  export const RequestBodySchema = z.object({
    enabled: z.boolean(),
  });
  export type RequestBody = z.infer<typeof RequestBodySchema>;
}
export namespace UploadLogo {
  export const ParamsSchema = z.object({
    id: z.coerce.number(),
    orientation: z.literal('landscape').or(z.literal('square')),
  });
  export type Params = z.infer<typeof ParamsSchema>;
}

export namespace DeleteLogo {
  export const ParamsSchema = z.object({
    id: z.coerce.number(),
    orientation: z.literal('landscape').or(z.literal('square')),
  });
  export type Params = z.infer<typeof ParamsSchema>;
}

// Account Chat Contexts
export const getAccountChatContextsRequestParamsSchema = z.object({
  accountId: z.coerce.number(),
});

export const getAccountChatContextsResponseBodySchema = z.array(z.object({
  id: z.coerce.number(),
  identifier: z.string(),
  name: z.string(),
  primaryAgent: z.object({
    id: z.coerce.number(),
    identifier: z.string(),
    name: z.string().optional(),
  }),
}));

export namespace GetAccountChatContexts {
  export type Params = z.infer<typeof getAccountChatContextsRequestParamsSchema>;
  export type Response = z.infer<typeof getAccountChatContextsResponseBodySchema>;
}

export const BulkCreatedUserSchema = z.object({
  id: z.coerce.number().nullable(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  errors: z.array(z.string()).prefault([]),
});
export type BulkUploadUser = z.infer<typeof BulkCreatedUserSchema>;

export const ParseUsersCsvRequestParamsSchema = z.object({
  accountId: z.coerce.number(),
});
export const ParseUsersCsvRequestBodySchema = z.object({
  file: z.instanceof(File),
});
export const ParseUsersCsvResponseBodySchema = z.object({
  users: z.array(BulkCreatedUserSchema),
});
export type ParseUsersCsvRequest = z.infer<typeof ParseUsersCsvRequestBodySchema> & z.infer<typeof ParseUsersCsvRequestParamsSchema>;

export const BulkUploadUsersRequestParamsSchema = z.object({
  accountId: z.coerce.number(),
});
export const BulkUploadUsersRequestBodySchema = z.object({
  brandIds: z.array(z.coerce.number()).prefault([]),
  users: z.array(BulkCreatedUserSchema),
});

export type BulkUploadUsersRequest = z.infer<typeof BulkUploadUsersRequestBodySchema> & z.infer<typeof BulkUploadUsersRequestParamsSchema>;
export const BulkUploadUsersResponseBodySchema = z.object({
  users: z.array(BulkCreatedUserSchema),
});